
import React, { useState } from 'react';
import { 
  Save, Image as ImageIcon, Palette, Monitor, Smartphone, 
  Loader2, ArrowLeft, X, Wand2, Bot, RotateCcw, RotateCw, Type
} from 'lucide-react';
import { ClientEntity } from '../types';
import { useIframeEditor, rgbToHex } from '../hooks/useIframeEditor';
import { ColorManager } from './editor/ColorManager';
import { AiSidebar } from './editor/AiSidebar';
import { MediaManager } from './editor/MediaManager';

interface SiteEditorProps {
  slug?: string;
  clientEntity?: ClientEntity;
  onClose?: () => void;
  hideCloseButton?: boolean;
}

export const SiteEditor: React.FC<SiteEditorProps> = ({ slug, clientEntity, onClose, hideCloseButton }) => {
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [activeTab, setActiveTab] = useState<'design' | 'content' | 'copilot'>('design');

  const {
    client,
    loading,
    saving,
    origemDoCanvas,
    specTipado,
    erroCarga,
    iframeKey,
    iframeContent,
    setIframeKey,
    history,
    historyIndex,
    detectedColors,
    detectedImages,
    activeElement,
    iframeRef,
    fileInputRef,
    pushToHistory,
    undo,
    redo,
    handleIframeLoad,
    captureSiteScreenshot,
    handleSave,
    updateColor,
    updateActiveElementStyle,
    triggerImageUpload,
    handleFileChange,
    getCurrentHtml
  } = useIframeEditor(clientEntity || null, slug);

  if (loading) return <div className="h-screen flex items-center justify-center bg-[#FAFAFA]"><Loader2 className="animate-spin text-black" size={40} /></div>;

  /*
    Falha de LEITURA nunca vira canvas editável.

    Abrir o rascunho aqui — que é o que o editor antigo faria, já que HTML
    ausente e HTML ilegível chegavam os dois como `''` — ofereceria publicar por
    cima de um site que existe e que não conseguimos ler. Melhor uma tela que diz
    o que houve do que um editor que apaga o trabalho de alguém.
  */
  if (erroCarga) {
    return (
      <div className={`flex items-center justify-center bg-[#FAFAFA] font-sans p-6 ${onClose ? 'fixed inset-0 z-50' : 'h-screen'}`}>
        <div className="bg-white border border-red-200 rounded-xl shadow-sm max-w-md w-full p-6">
          <h1 className="text-base font-semibold text-red-900">Não consegui abrir o site</h1>
          <p className="text-sm text-gray-600 mt-2 leading-relaxed">{erroCarga}</p>
          <p className="text-xs text-gray-500 mt-3 leading-relaxed">
            O editor não abre um rascunho aqui de propósito: se o site existe e só não deu
            para lê-lo, publicar por cima apagaria o que está no ar.
          </p>
          <div className="flex gap-2 mt-5">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-md bg-black text-white text-xs font-medium hover:bg-gray-900 transition-colors"
            >
              Tentar de novo
            </button>
            {onClose && (
              <button onClick={onClose} className="px-4 py-2 rounded-md border border-gray-200 text-gray-600 text-xs font-medium hover:bg-gray-50 transition-colors">
                Fechar
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!client) return <div className="h-screen flex items-center justify-center font-sans">Cliente não encontrado.</div>;

  const safeHex = (colorStr: string) => {
    if (!colorStr || colorStr === 'rgba(0, 0, 0, 0)' || colorStr === 'transparent') return '#ffffff';
    if (colorStr.startsWith('#')) return colorStr;
    try {
      return rgbToHex(colorStr);
    } catch {
      return '#ffffff';
    }
  };

  return (
    <div className={`flex overflow-hidden bg-[#FAFAFA] font-sans ${onClose ? 'fixed inset-0 z-50' : 'h-screen'}`}>
      
      <div className="w-[400px] bg-white border-r border-gray-200 flex flex-col z-20 shadow-xl shrink-0">
        <div className="p-5 border-b border-gray-100 bg-white">
           <div className="flex justify-between items-start mb-2">
             {!hideCloseButton && (
               onClose ? (
                 <button onClick={onClose} className="flex items-center gap-1 text-gray-400 hover:text-gray-900 transition-colors">
                   <X size={16}/> <span className="text-[10px] font-semibold uppercase tracking-widest">Fechar</span>
                 </button>
               ) : (
                 <div className="flex items-center gap-2 text-black cursor-pointer hover:underline" onClick={() => window.history.back()}>
                   <ArrowLeft size={16}/> <span className="text-[10px] font-semibold uppercase tracking-widest">Dashboard</span>
                 </div>
               )
             )}
             <div className={`flex gap-2 ${hideCloseButton ? 'ml-auto' : ''}`}>
               <button 
                 onClick={undo} 
                 disabled={historyIndex <= 0}
                 className="p-1.5 rounded-md border border-gray-200 text-gray-400 hover:text-black hover:bg-gray-50 transition-all disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                 title="Desfazer (Undo)"
               >
                 <RotateCcw size={16} />
               </button>
               <button 
                 onClick={redo} 
                 disabled={historyIndex >= history.length - 1}
                 className="p-1.5 rounded-md border border-gray-200 text-gray-400 hover:text-black hover:bg-gray-50 transition-all disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                 title="Refazer (Redo)"
               >
                 <RotateCw size={16} />
               </button>
             </div>
           </div>
           <h1 className="font-semibold text-xl text-gray-900 tracking-tight flex items-center gap-2">
              Ditho <span className="text-gray-500">Copilot</span>
              <span className="bg-gray-100 text-gray-600 border border-gray-200 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider animate-pulse">Vision Active</span>
           </h1>
        </div>

        {/* Active Element Quick Edit Panel */}
        {activeElement && (
          <div className="p-5 border-b border-blue-100 bg-blue-50/30 animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-blue-600 flex items-center gap-2">
                <Wand2 size={14} /> Elemento Selecionado
              </h3>
              <span className="text-[10px] font-mono bg-blue-100 text-blue-700 px-2 py-0.5 rounded border border-blue-200">
                &lt;{activeElement.tagName}&gt;
              </span>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between bg-white p-2.5 rounded-lg border border-blue-100 shadow-sm">
                <span className="text-xs font-medium text-gray-700 flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: safeHex(activeElement.bgColor) }}></div>
                  Cor do Fundo
                </span>
                <div className="relative w-8 h-8 rounded-md overflow-hidden border border-gray-200 shadow-sm cursor-pointer hover:scale-105 transition-transform">
                  <input 
                    type="color" 
                    value={safeHex(activeElement.bgColor)} 
                    onChange={(e) => updateActiveElementStyle('backgroundColor', e.target.value)}
                    className="absolute -top-2 -left-2 w-12 h-12 cursor-pointer"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between bg-white p-2.5 rounded-lg border border-blue-100 shadow-sm">
                <span className="text-xs font-medium text-gray-700 flex items-center gap-2">
                  <Type size={14} className="text-gray-400" />
                  Cor do Texto
                </span>
                <div className="relative w-8 h-8 rounded-md overflow-hidden border border-gray-200 shadow-sm cursor-pointer hover:scale-105 transition-transform">
                  <input 
                    type="color" 
                    value={safeHex(activeElement.textColor)} 
                    onChange={(e) => updateActiveElementStyle('color', e.target.value)}
                    className="absolute -top-2 -left-2 w-12 h-12 cursor-pointer"
                  />
                </div>
              </div>
            </div>
            <p className="text-[10px] text-blue-500/70 mt-3 text-center">
              Dica: Você também pode clicar no texto do site para editá-lo diretamente.
            </p>
          </div>
        )}

        {/*
          A terceira aba existia no codigo e nao tinha botao.

          `AiSidebar` so era renderizada com `activeTab === 'copilot'`, e nenhum
          controle da tela definia esse valor: o copiloto — o que da nome ao
          painel inteiro, "Ditho Copilot", com o selo "Vision Active" logo acima —
          era codigo morto na interface.
        */}
        <div className="flex border-b border-gray-100 bg-gray-50/50">
          <button onClick={() => setActiveTab('design')} className={`flex-1 py-4 text-[10px] font-semibold uppercase tracking-widest flex items-center justify-center gap-2 transition-all ${activeTab === 'design' ? 'text-black border-b-2 border-black bg-white' : 'text-gray-400 hover:text-gray-600'}`}><Palette size={16} /> Cores</button>
          <button onClick={() => setActiveTab('content')} className={`flex-1 py-4 text-[10px] font-semibold uppercase tracking-widest flex items-center justify-center gap-2 transition-all ${activeTab === 'content' ? 'text-black border-b-2 border-black bg-white' : 'text-gray-400 hover:text-gray-600'}`}><ImageIcon size={16} /> Mídia</button>
          <button onClick={() => setActiveTab('copilot')} className={`flex-1 py-4 text-[10px] font-semibold uppercase tracking-widest flex items-center justify-center gap-2 transition-all ${activeTab === 'copilot' ? 'text-black border-b-2 border-black bg-white' : 'text-gray-400 hover:text-gray-600'}`}><Bot size={16} /> Copiloto</button>
        </div>

        {origemDoCanvas === 'novo' && (
          <div className="mx-5 mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-[11px] text-amber-800 leading-relaxed">
            <b>Este cliente ainda não tem site.</b> O que está no canvas é um ponto de
            partida montado com os dados do negócio — edite à vontade e use
            <b> Publicação Final</b> para criar a primeira versão.
          </div>
        )}

        {specTipado && (
          <div className="mx-5 mt-4 bg-orange-50 border border-orange-200 rounded-lg p-3 text-[11px] text-orange-900 leading-relaxed">
            <b>Este site tem estrutura por seções.</b> O editor visual trabalha com a
            página inteira, então <b>publicar por aqui converte a estrutura em HTML</b> —
            as seções deixam de existir como blocos. Para mexer só no texto ou na cor,
            tudo bem; para reorganizar seções, use a fábrica.
          </div>
        )}

        {origemDoCanvas === 'rascunho' && (
          <div className="mx-5 mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 text-[11px] text-blue-800 leading-relaxed">
            <b>Este site nunca foi publicado.</b> Você está editando o rascunho que já
            existia — nada disso está no ar ainda. <b>Publicação Final</b> coloca no ar.
          </div>
        )}

        <div className="flex-1 overflow-y-auto bg-white relative flex flex-col">
          {activeTab === 'copilot' && (
             <AiSidebar 
                getCurrentHtml={getCurrentHtml}
                pushToHistory={pushToHistory}
                setIframeKey={setIframeKey}
                captureSiteScreenshot={captureSiteScreenshot}
                activeTab={activeTab}
             />
          )}

          {activeTab === 'design' && (
            <ColorManager 
                detectedColors={detectedColors} 
                updateColor={updateColor} 
            />
          )}

          {activeTab === 'content' && (
            <MediaManager 
               detectedImages={detectedImages}
               onReplaceImage={triggerImageUpload}
            />
          )}
        </div>

        <div className="p-6 border-t border-gray-100 bg-white">
          <button onClick={handleSave} disabled={saving} className="w-full flex items-center justify-center gap-2 bg-black hover:bg-gray-900 text-white py-3 rounded-md font-medium transition-all disabled:opacity-70">
             {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Publicação Final
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col bg-[#FAFAFA] h-full overflow-hidden relative">
        <div className="h-14 bg-white border-b border-gray-200 flex items-center justify-center gap-4 shadow-sm z-10 shrink-0">
           <button onClick={() => setDevice('desktop')} className={`px-3 py-1.5 rounded-md transition-all flex items-center gap-2 text-sm font-medium ${device === 'desktop' ? 'bg-gray-100 text-black' : 'text-gray-500 hover:text-gray-900'}`}><Monitor size={16} /> Desktop</button>
           <button onClick={() => setDevice('mobile')} className={`px-3 py-1.5 rounded-md transition-all flex items-center gap-2 text-sm font-medium ${device === 'mobile' ? 'bg-gray-100 text-black' : 'text-gray-500 hover:text-gray-900'}`}><Smartphone size={16} /> Mobile</button>
        </div>

        <div className="flex-1 flex items-center justify-center p-6 md:p-10 overflow-auto bg-[#FAFAFA]">
           <div className={`bg-white shadow-xl transition-all duration-500 overflow-hidden relative ${device === 'mobile' ? 'w-[375px] h-[667px] rounded-[40px] border-[12px] border-black shrink-0' : 'w-full h-full max-w-[1280px] rounded-xl border border-gray-200'}`}>
             {device === 'mobile' && <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-black rounded-b-2xl z-20"></div>}
             <iframe ref={iframeRef} key={iframeKey} srcDoc={iframeContent} title="Canvas" className="w-full h-full bg-white" onLoad={handleIframeLoad} sandbox="allow-scripts allow-same-origin allow-popups allow-forms" />
           </div>
        </div>
      </div>

      <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />
    </div>
  );
};
